import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useTypedParams } from "react-router-typesafe-routes";

import { extractExactI18nLabel } from "~/common/functions";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { EditMapToolsProvider } from "~/features/recordGeoItems/contexts/EditMapToolsContext";
import {
  toAreaGeoItemEntryRows,
  useAreaGeoItemDetail,
  useAreaInputContext,
  useRecordGeoItemEdit,
} from "~/features/recordGeoItems/hooks";
import type { RecordGeoItemFormData } from "~/features/recordGeoItems/types";
import { parseGeometry } from "~/features/recordGeoItems/utils/parseGeometry";
import { parseH3IndexesStr } from "~/features/recordGeoItems/utils/parseH3IndexStr";
import { v2Route } from "~/v2/routes";
import { RecordGeoItemForm } from "./RecordGeoItemForm";

/** Edit an existing geo-item area. Mounted at `/area-input/:recordId/:geoItemId/edit`. */
export function RecordGeoItemEdit() {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const navigate = useNavigate();
  const { recordId = "", geoItemId = "" } = useTypedParams(v2Route.areaInput.record.detail);
  const rgi = useAreaGeoItemDetail(recordId, geoItemId);
  const { data: context } = useAreaInputContext();
  const detailPath = v2Route.areaInput.record.detail.$buildPath({ params: { recordId, geoItemId } });
  // Cnut refuses to change a description the record's Evidence holds, so the form only shows it.
  const attributedEntries = useMemo(
    () => (rgi?.entries.some((entry) => entry.attributed) ? toAreaGeoItemEntryRows(rgi.entries, locale) : undefined),
    [rgi, locale],
  );

  const { submit, processing } = useRecordGeoItemEdit({
    recordId,
    geoItemId,
    withDescription: !attributedEntries,
    onEdited: () => navigate(detailPath),
  });

  const defaultValues = useMemo<RecordGeoItemFormData | null>(() => {
    if (!rgi) return null;
    // The form writes one description; a legacy-only pair has exactly one entry, its join row's.
    const entry = rgi.entries[0];
    const i18nLabel = (entry?.i18nLabel ?? null) as Record<string, unknown> | null;
    return {
      date: entry?.date ?? "",
      endDate: entry?.endDate ?? "",
      jaLabel: extractExactI18nLabel(i18nLabel, "ja") ?? "",
      enLabel: extractExactI18nLabel(i18nLabel, "en") ?? "",
      h3Indexes: parseH3IndexesStr(rgi.geoItem.h3Indexes ?? null),
      geometry: parseGeometry(rgi.geoItem.geometry ?? null),
      memo: (entry?.metadata as { memo?: string } | undefined)?.memo ?? "",
      evidences: rgi.evidences.map((evidence) => {
        const reference = evidence.references[0] ?? null;
        return {
          id: evidence.id,
          reference: reference ? { id: reference.id, title: reference.title } : null,
          citation: (evidence.citation as { page?: string } | null) ?? undefined,
        };
      }),
    };
  }, [rgi]);

  if (!rgi || rgi.deactivatedAt || !defaultValues) {
    return <div className="mx-auto max-w-[var(--content-max)] p-8 text-[13px] text-fg-3">{t("ui.notFound")}</div>;
  }

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <h1 className="text-dewey-title capitalize text-fg-1">
        {t("ui.edit")} {t("model.geoItem.modelName")}
      </h1>
      <EditMapToolsProvider>
        <RecordGeoItemForm
          references={context.references}
          defaultValues={defaultValues}
          processing={processing}
          submitLabel={t("ui.save")}
          attributedEntries={attributedEntries}
          onSubmit={(form) => void submit(form)}
          onCancel={() => navigate(detailPath)}
        />
      </EditMapToolsProvider>
    </div>
  );
}
